import React from "react";

import { useMsal } from "@azure/msal-react";
import { Icon } from "@iconify/react";
import {
  Avatar,
  Box,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";

import { useGetTeamsUserData } from "@/api/useGetTeamsUserData";
import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";

interface UserMenuProps {
  callbackClose: (_update: boolean) => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ callbackClose }) => {
  const theme = useTheme();
  const { msalConfig } = useTeamsAndChannels();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { userData, userPhotoUrl, loading } = useGetTeamsUserData(msalConfig.loginRequest);
  const { instance } = useMsal();

  const handleLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      const accounts = instance.getAllAccounts();
      const isLoggedIn = accounts.length > 0;
      if (!isLoggedIn) {
        callbackClose(false);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <Box
      border="1px solid #D0D0CE"
      height="68px"
      padding="16px 24px"
      gap="8px"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
    >
      <Box display="flex" alignItems="center" gap="8px">
        <Avatar
          alt="Teams User data"
          src={userPhotoUrl || ""}
          sx={{ width: "24px", height: "24px" }}
        />
        {loading ? (
          <Skeleton data-testid="user_name_skeleton" width={150} height={30} />
        ) : (
          <Typography fontWeight="bold">{userData?.displayName}</Typography>
        )}
      </Box>
      <button
        data-testid={`teams-integration-user-menu-trigger`}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        style={{
          backgroundColor: menuOpen ? theme.palette.primary.main : "transparent",
          border: "0px",
          borderRadius: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0",
          cursor: "pointer",
        }}
      >
        <Icon
          icon="iconamoon:menu-kebab-vertical-bold"
          width="25"
          height="25"
          color={menuOpen ? "white" : theme.palette.primary.main}
        />
      </button>
      <Menu
        id="teams-integration-user-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.1)",
            borderRadius: "0px",
            transform: "translateX(30px)",
            minWidth: "160px",
          },
        }}
      >
        <MenuItem data-testid="teams_logout" onClick={handleLogout}>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
